import ProjectData from '@/data/ProjectData.ts'

export default [

    new ProjectData(
    "epic-pro-support",
    "Epic Pro Support (via BaboonLab)",
    "img/projects/EPS-6.png",
    `
    <div class="paragraph">
        As part of <strong>BaboonLab</strong>, I worked as an <strong>Unreal Engine Support Engineer</strong> in the <strong>Epic Pro Support</strong> program, assisting studios and licensees worldwide with complex Unreal Engine issues.
    </div>

    <div class="paragraph">
        My work focused on investigating and reproducing difficult problems across multiple domains, including <strong>gameplay</strong>, <strong>UI</strong>, <strong>input</strong>, <strong>physics</strong>, <strong>Niagara</strong>, and <strong>crash-related issues</strong>. I regularly built clean repro projects, analyzed engine behavior through <strong>source builds</strong>, and used debugging tools and <strong>call stack analysis</strong> to identify likely root causes.
    </div>

    <div class="paragraph">
        In addition to technical investigation, I proposed practical workarounds, documented findings, and coordinated with external studios and Epic engineers through bug reports and internal communication channels. This role strengthened my ability to diagnose engine-level problems and communicate solutions clearly in production contexts.
    </div>

    <div class="paragraph">
        <ul>
            <li><b>Period:</b> September 2024 – March 2026</li>
            <li><b>Focus:</b> Unreal Engine debugging, reproduction, technical support, and production issue investigation</li>
            <li><b>Key tools:</b> UE source builds, Visual Studio debugging, call stacks, repro projects, Jira</li>
        </ul>
    </div>

    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/EPS-1.png"/>
        <img class="pc-screenshot" src="img/projects/EPS-2.png"/>
        <img class="pc-screenshot" src="img/projects/EPS-3.png"/>
        <img class="pc-screenshot" src="img/projects/EPS-4.png"/>
    </div>

    <div class="paragraph">
        <em>Note: this work involved confidential studio cases, so visuals are representative only or may be omitted.</em>
    </div>
    `,
    ['UE5', 'C++', 'Debugging'],
    "#1f3a5f",
    false,
    true
),

    new ProjectData(
    "el-ultimo-viaje",
    "El Último Viaje",
    "img/projects/ElUltimoViaje.jpg",
    `
    <div class="paragraph">
        <strong>El Último Viaje</strong> is a game developed for <strong>Patrimonio Game Jam 2025</strong>, a national competition organized by the Peruvian Ministry of Culture.
    </div>

    <div class="paragraph">
        The project was developed in <strong>Unreal Engine</strong> as a collaborative jam entry and was awarded <strong>1st place</strong> for its design, execution, and overall experience.
    </div>

    <div class="paragraph">
        I was the <strong>sole programmer</strong> on the project, responsible for implementing the gameplay systems, interactions, UI flow, and overall technical integration needed to bring the game from concept to a polished jam submission under tight production constraints.
    </div>

    <div class="paragraph">
        <ul>
            <li><b>Project date:</b> September 2025</li>
            <li><b>Recognition:</b> 1st Place — Patrimonio Game Jam 2025</li>
            <li><b>Engine:</b> Unreal Engine 5</li>
        </ul>
    </div>

    <div class="paragraph">
        <div class="notice">
            Windows build available on <a href="https://ffabian.itch.io/elultimoviaje" target="_blank">itch.io</a>.
        </div>
    </div>

    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/ElUltimoViaje-1.png"/>
        <img class="pc-screenshot" src="img/projects/ElUltimoViaje-2.png"/>
        <img class="pc-screenshot" src="img/projects/ElUltimoViaje-3.png"/>
    </div>
    `,
    ['UE5', 'Game Jam Winner', 'Culture'],
    "#1f3a5f",
    false,
    false
),

    new ProjectData(
        "fortnite-ng",
        "Fortnite (via Nimble Giant)",
        "img/projects/fortnite.jpg",
        `
    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/nimblegiant_logo.jpg" alt="Nimble Giant logo" style="max-width: 200px;" />
        <img class="pc-screenshot" src="img/projects/logo-fortnightepic.png" alt="Fortnite by Epic Games" />
    </div>

    <div class="paragraph">
        While working at <strong>Nimble Giant Entertainment</strong>, I contributed to production work related to <strong>Fortnite Creative</strong> in collaboration with Epic.
    </div>

    <div class="paragraph">
        My work focused on investigating and debugging <strong>crashes</strong>, <strong>hangs</strong>, and complex runtime behaviors affecting production environments. This involved working with <strong>C++</strong>, <strong>Verse</strong>, Unreal Engine systems, and Fortnite-specific codepaths to help identify root causes and support stability.
    </div>

    <div class="paragraph">
        I also set up and maintained local development environments connected to backend services for testing and validation, and worked closely with internal teams and Epic engineers in a large-scale live production context.
    </div>

    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/Creative.jpg" alt="Creative"/>
        <img class="pc-screenshot" src="img/projects/UEFN_Devices.jpg" alt="Devices" />
    </div>

    <div class="paragraph"><em>Note: due to NDA, visuals are limited.</em></div>
    `,
        ['UE5', 'C++', 'Verse', 'Fortnite Creative', 'Perforce'],
        "#1f3a5f",
        false,
        true
    ),

    new ProjectData(
        "acuarella",
        "Acuarella: The Videogame",
        "img/projects/AcuarellaBox.png",
        `
        <div class="paragraph center">
            <img class="pc-screenshot" src="img/projects/Acuarella-2.png" alt="Acuarella Screenshot" />
        </div>

        <div class="paragraph">
            <strong>Acuarella</strong> is a digital adaptation of the original board game created by Karem Potochnik — a competitive strategy game built around polyomino placement, color combinations, and special card-based interactions.
        </div>

        <div class="paragraph">
            I am the <strong>sole programmer</strong> on the project, working closely with a visual artist to turn the original tabletop concept into a polished interactive prototype that can support playtesting, pitching, and potential publishing conversations.
        </div>

        <div class="paragraph">
            The game was developed in <strong>Unreal Engine 5</strong> using both <strong>C++</strong> and <strong>Blueprints</strong>, and includes full <strong>online multiplayer</strong> for two players. I implemented the gameplay flow, replication logic, and asynchronous multiplayer behavior with a strong focus on reliability and responsiveness.
        </div>

        <div class="paragraph">
            A dedicated backend was deployed using <strong>AWS</strong> to support session hosting and matchmaking. The project has been released privately for targeted playtesting and publisher-facing evaluation.
        </div>

        <div class="paragraph center">
            <img class="pc-screenshot" src="img/projects/Acuarella2.png" alt="Acuarella Screenshot" />
            <img class="pc-screenshot" src="img/projects/Acuarella3.png" alt="Acuarella Screenshot" />
        </div>
        `,
        ['UE5', 'C++', 'Blueprints', 'Multiplayer', 'AWS'],
        "#1f3a5f",
        false,
        false
    ),

    new ProjectData(
        "mining-truck-simulator",
        "Mining Truck Simulator",
        "img/projects/MiningTruck-1.png",
        `
        <div class="paragraph">
            A Caterpillar truck simulator developed for the mining industry while working with <strong>Proximity</strong>. The experience was built for immersive operator training and deployed to <strong>Meta Quest</strong> devices.
        </div>

        <div class="paragraph">
            My role focused on implementing gameplay and interaction features in <strong>Unreal Engine 5</strong> using <strong>C++</strong>, integrating UI and truck interaction systems, and preparing builds for standalone VR hardware.
        </div>

        <div class="paragraph">
        <ul>
            <li><b>Project date:</b> March to May 2024</li>
            <li><b>Key technologies:</b> UE5, C++, Meta Quest 3, vehicle simulation</li>
            <li>Implemented core game modes, truck interaction elements, UI features, controller integration, and general asset integration.</li>
            <li>Built as an immersive training tool focused on realistic vehicle controls and environmental interaction.</li>
        </ul>
        </div>

        <div class="paragraph center">
            <img class="pc-screenshot" src="img/projects/MiningAssets-1.png" alt="Mining Truck Simulator Screenshot 1" />
            <img class="pc-screenshot" src="img/projects/MiningAssets-2.png" alt="Mining Truck Simulator Screenshot 2" />
            <img class="pc-screenshot" src="img/projects/MiningAssets-3.png" alt="Mining Truck Simulator Screenshot 3" />
        </div>
        `,
        ['UE5', 'C++', 'VR', 'Meta Quest', 'Immersive Training'],
        "#1f3a5f",
        false,
        false
    ),

    new ProjectData(
        "light-signal",
        "Light Signal",
        "img/projects/LightSignalLogo.png",
        `
    <div class="paragraph">
        <strong>Light Signal</strong> was developed in 7 days for the <strong>Epic MegaJam 2023</strong> in collaboration with a 3D artist. I was responsible for gameplay programming, systems implementation, and general asset integration.
    </div>

    <div class="paragraph">
    <div class="warning">
        This project was selected among more than 500 global entries as the winner of the <b>Speed of Light</b> modifier category, recognizing its use of Nvidia technologies such as <b>DLSS</b> and <b>ray tracing</b>.
    </div>
    </div>

    <span>Gameplay footage:</span>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/HXkAEPGqwaE" frameborder="0" allowfullscreen></iframe>
    </div>

    <div class="paragraph">
    <ul>
        <li><b>Project date:</b> September 2023</li>
        <li>Developed in <strong>Unreal Engine 5</strong> during the Epic MegaJam.</li>
        <li>I implemented the gameplay mechanics and integrated assets including models, animation, audio, and logic under strict time constraints.</li>
        <li>I also contributed to scope definition and game design decisions during development.</li>
    </ul>
    </div>

    <div class="paragraph">
    <div class="notice">
        Windows build available on <a href="https://ffabian.itch.io/light-signal" target="_blank">itch.io</a>.
    </div>
    </div>

    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/LightSignal1.jpg" alt="LightSignal Screenshot" />
        <img class="pc-screenshot" src="img/projects/LightSignal2.jpg" alt="LightSignal Screenshot" />
        <img class="pc-screenshot" src="img/projects/LightSignal3.jpg" alt="LightSignal Screenshot" />
    </div>
    `,
        ['UE5', 'Epic MegaJam Winner', 'DLSS', 'Ray Tracing'],
        "#1f3a5f",
        false,
        false
    ),

    new ProjectData(
        "rokit-games",
        "ROKiT Games",
        "img/projects/rokit_logo.png",
        `
    <div class="paragraph">
        At <a href="https://rokitstudios.com/" target="_blank">ROKiT Games</a>, I worked as an Unreal Engine programmer on gameplay systems and reusable project functionality using <strong>C++</strong> and <strong>Blueprints</strong>.
    </div>

    <div class="paragraph">
        My work included gameplay programming, UI, AI-related features, animation blueprint support, rapid prototyping, and asset integration. I also contributed tooling support for the broader team, including Blender scripting and general Unreal pipeline assistance.
    </div>

    <span>Bible animation project: assembled in UE Sequencer using MetaHuman and Megascans assets.</span>

    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/1Zp9o9rjo68" frameborder="0" allowfullscreen></iframe>
    </div>

    <span>Snow White animation project: rendered with UE Sequencer and assembled in Blender.</span>

    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/Jjy36P84Iw4" frameborder="0" allowfullscreen></iframe>
    </div>

    <span>Jack Chirp animation shorts: rendered in Blender with pipeline support tied to Unreal-related workflows.</span>

    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/zUMhMDBtF8k" frameborder="0" allowfullscreen></iframe>
    </div>
    `,
        ['UE5', 'C++', 'Blueprints', 'Gameplay Systems', 'Sequencer'],
        "#1f3a5f",
        false,
        false
    ),

    new ProjectData(
        "delta-upamayo",
        "Delta Upamayo Experience",
        "img/projects/DeltaSimple.png",
        `
    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/DeltaLandscapeLarge.png" alt="Delta Upamayo" />
    </div>

    <div class="paragraph">
        A short VR experience developed for <a href="https://www.linkedin.com/company/proximity.dev/" target="_blank">Proximity</a> and <a href="https://www.linkedin.com/company/amsac/" target="_blank">Activos Mineros</a>, designed to communicate the impact of a remediation project in Perú through immersive presentation.
    </div>

    <span>Gameplay video reel:</span>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/h2aPd9ZfxKQ" frameborder="0" allowfullscreen></iframe>
    </div>

    <div class="paragraph">
    <ul>
        <li><b>Project date:</b> August to September 2023</li>
        <li>Developed using <strong>UE5</strong> and deployed to <strong>Meta Quest 2</strong>.</li>
        <li>I developed key features using <strong>C++</strong> and <strong>Blueprints</strong>, including the core sequence flow, a 360 video level, UI widgets capable of displaying image and video, and dynamic lighting fade transitions.</li>
        <li>The experience was showcased at a mining-industry convention and also received attention from Peruvian <a href="https://youtu.be/79oT0uYBEcA?si=jUB-9yC_Pc3jkT9x" target="_blank">national TV</a>.</li>
        <li>Additional information is available in this <a href="https://www.amsac.pe/amsac-presento-experiencia-de-remediacion-de-pasivos-mineros-con-realidad-virtual-en-perumin-36-v2/" target="_blank">article</a>.</li>
    </ul>
    </div>

    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/project-delta1.png" alt="DeltaUpamayo Screenshot 1" />
        <img class="pc-screenshot" src="img/projects/project-delta2.png" alt="DeltaUpamayo Screenshot 2" />
        <img class="pc-screenshot" src="img/projects/project-delta3.png" alt="DeltaUpamayo Screenshot 3" />
        <img class="pc-screenshot" src="img/projects/project-delta4.png" alt="DeltaUpamayo Screenshot 4" />
        <img class="pc-screenshot" src="img/projects/project-delta5.gif" alt="DeltaUpamayo Screenshot 5" />
    </div>
    `,
        ['UE5', 'C++', 'Blueprints', 'VR', 'Meta Quest'],
        "#1f3a5f",
        false,
        false
    ),

    new ProjectData(
        "metahuman-assistant",
        "Metahuman Assistant",
        "img/projects/Metahuman.gif",
        `
    <div class="paragraph">
        A UE5 application built around <strong>MetaHuman</strong> technology, designed to deliver lifelike avatar interaction through advanced <strong>text-to-speech</strong>, <strong>lip sync</strong>, and real-time presentation features.
    </div>

    <div class="paragraph">
        The project was intended to connect with a larger web platform combining conversational AI, audio generation, dynamic backgrounds, and photorealistic characters.
    </div>

    <span>Demo preview:</span>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/uTIQtG0JGOQ" frameborder="0" allowfullscreen></iframe>
    </div>

    <div class="paragraph">
    <ul>
        <li><b>Project date:</b> May to July 2023</li>
        <li>Developed in <strong>UE5</strong> as a freelance project.</li>
        <li>Implemented TTS and lip sync generation using the MetaHuman SDK.</li>
        <li>Supported multiple MetaHuman skins at runtime.</li>
        <li>Added dynamic image and video backgrounds based on external URLs.</li>
        <li>Built for a <strong>Pixel Streaming</strong> architecture, including two-way communication with the player page based on runtime events.</li>
    </ul>
    </div>

    <div class="paragraph">
        I was the <strong>sole programmer</strong> on this application and delivered it as a production-ready Unreal prototype for a medical-field client.
    </div>
    `,
        ['UE5', 'MetaHuman', 'Pixel Streaming', 'TTS', 'Lip Sync'],
        "#1f3a5f",
        false,
        false
    ),

    new ProjectData(
        "real-life-learning",
        "Real Life Learning",
        "img/projects/RLLPlayer3.gif",
        `
    <div class="paragraph">
        <strong>Real Life Learning</strong> is a VR training application developed for a private power company in Perú. The project was designed to train operators through interactive objectives and scenario-based learning.
    </div>

    <span>Gameplay footage:</span>
    <div class="paragraph center">
        <iframe class="youtube" src="https://www.youtube.com/embed/RpEvf5qxKDo" frameborder="0" allowfullscreen></iframe>
    </div>

    <div class="paragraph">
        As part of <a href="https://www.linkedin.com/company/grupo-avatar-pucp/" target="_blank">Grupo Avatar</a>, I contributed gameplay and interaction features in <strong>Unreal Engine 4</strong> using <strong>C++</strong> and <strong>Blueprints</strong>.
    </div>

    <div class="paragraph">
    <ul>
        <li><b>Project date:</b> 2022</li>
        <li>Implemented gameplay actors, components, and game mode logic.</li>
        <li>Worked on UI using UMG, animation blueprints, asset integration, and tutorial-related VR design support.</li>
        <li>Targeted platforms included HTC Vive and Oculus Rift S.</li>
        <li>Developed in collaboration with designers, artists, and other developers, using Jira and Scrum.</li>
        <li>The application entered real-world use as a training tool for power plant operators.</li>
    </ul>
    </div>

    <div class="paragraph">
        An article about the project was published (in Spanish) <a href="https://investigacion.pucp.edu.pe/institucionales/real-life-learning-la-realidad-virtual-en-la-industria/" target="_blank">here</a>.
    </div>

    <div class="paragraph center">
        <img class="pc-screenshot" src="img/projects/project-rll1.png" alt="RLL Screenshot 1" />
        <img class="pc-screenshot" src="img/projects/project-rll2.jpg" alt="RLL Screenshot 2" />
        <img class="pc-screenshot" src="img/projects/project-rll3.jpg" alt="RLL Screenshot 3" />
    </div>
    `,
        ['UE4', 'C++', 'Blueprints', 'VR', 'Training'],
        "#1f3a5f",
        false,
        false
    ),
];